import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { apiGet } from "@/lib/api";
import { BOOK, EVENT } from "@/lib/data";

const Confirmation = () => {
  const { code } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    apiGet(`/api/orders/${code}`)
      .then(setOrder)
      .catch(() => setError(true));
  }, [code]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <LogoMark size={56} />
        <h1 className="font-display text-3xl mt-8 text-[#F8F9FA]">Order not found</h1>
        <p className="text-[#8A99B5] mt-3">Double-check your confirmation link.</p>
        <Link to="/" className="btn-gold rounded-full px-8 py-3.5 text-sm font-bold mt-8">
          Back to the Event
        </Link>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16 px-5 sm:px-8" style={{ background: "radial-gradient(ellipse 70% 50% at 50% 0%, #1a2450 0%, #0B1021 65%)" }}>
      <div className="max-w-2xl mx-auto text-center">
        <Link to="/" className="inline-flex items-center gap-3" data-testid="confirmation-logo-link">
          <LogoMark size={52} />
        </Link>
        <div className="mt-8 inline-flex items-center gap-2 text-emerald-400 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-5 py-2 text-sm">
          <CheckCircle2 size={16} /> Seats confirmed & held for you
        </div>
        <h1 className="font-display text-4xl sm:text-5xl text-[#F8F9FA] mt-6">
          {order.is_gift ? "Your gift is wrapped" : "The light has your seat"}
        </h1>
        <p className="text-[#8A99B5] font-light mt-4">
          Confirmation <span className="font-cinzel text-[#F3D068] tracking-[0.2em]">{order.code}</span> · sent to{" "}
          {order.buyer.email}
        </p>

        {/* Ticket stub */}
        <div className="ticket-notch relative mt-10 rounded-3xl border border-[#D4AF37]/35 bg-gradient-to-b from-[#182243] to-[#131B36] p-8 text-left">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="eyebrow">{EVENT.dateLabel}</p>
              <p className="font-display text-2xl text-[#F8F9FA] mt-2">{EVENT.venue}</p>
              <p className="text-sm text-[#8A99B5]">{EVENT.address}</p>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-[0.25em] text-[#8A99B5]">Doors {EVENT.doors}</p>
              <p className="font-cinzel text-xl text-[#F3D068] mt-1">Show {EVENT.showStart}</p>
            </div>
          </div>
          <div className="hairline my-6" />
          <div className="grid sm:grid-cols-2 gap-3">
            {order.tickets.map((t) => (
              <div key={t.seat_id} className="border border-white/10 rounded-xl px-4 py-3 flex items-center justify-between" data-testid={`confirmation-seat-${t.seat_id}`}>
                <span className="text-sm text-[#C5CEE0]">
                  <span className={t.zone === "vip" ? "text-[#F3D068]" : ""}>{t.zone_label}</span> · Row {t.row}, Seat {t.seat_number}
                </span>
                <span className="text-sm text-[#F8F9FA] font-medium">${t.price}</span>
              </div>
            ))}
          </div>
          {order.book_addon && (
            <p className="text-sm text-[#F3D068] mt-5 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#F3D068]" /> Signed copy of Infinite Life, Infinite Lessons waiting at intermission
            </p>
          )}
          <div className="flex items-center justify-between mt-6">
            <span className="text-[#8A99B5] text-sm">Total</span>
            <span className="font-display text-3xl text-gold-gradient">${order.total}</span>
          </div>
        </div>

        {/* Gift card preview */}
        {order.is_gift && (
          <div className="mt-8 rounded-3xl border border-[#D4AF37]/30 bg-[radial-gradient(ellipse_at_top,rgba(124,116,242,0.2),rgba(19,27,54,0.9))] p-8 text-left">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#F3D068]">A Holiday Gift For You · A View from the Top — Live</p>
            <p className="font-display italic text-2xl text-[#F8F9FA] mt-4 leading-snug">
              {order.recipient.name}, {order.recipient.message || "this seat is saved for you — an evening with the other side, and a night off from being strong."}
            </p>
            <p className="text-xs text-[#8A99B5] mt-5">
              With love from {order.buyer.first_name} {order.buyer.last_name} · delivered to {order.recipient.email}
            </p>
          </div>
        )}

        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <Link to="/" data-testid="back-home-button" className="btn-ghost-gold rounded-full px-8 py-3.5 text-sm font-medium">
            Back to the Event
          </Link>
          <a
            href={BOOK.amazonUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="confirmation-book-button"
            className="btn-gold rounded-full px-8 py-3.5 text-sm font-bold"
          >
            Get the Bestselling Book
          </a>
        </div>
        <p className="text-xs text-[#8A99B5] mt-8 font-light">
          Questions? Call the Saban Theatre at {EVENT.venuePhone} · All ticket sales final.
        </p>

        {/* Post-purchase funnel */}
        <div className="mt-10 glass rounded-3xl p-7 text-left">
          <p className="eyebrow text-center">While You Wait for January 14th</p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <a
              href="https://www.instagram.com/susangrauofficial"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="confirmation-social-instagram"
              className="btn-ghost-gold rounded-full px-5 py-2.5 text-xs font-medium"
            >
              Instagram @susangrauofficial
            </a>
            <a
              href="https://www.youtube.com/@susangrauofficial"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="confirmation-social-youtube"
              className="btn-ghost-gold rounded-full px-5 py-2.5 text-xs font-medium"
            >
              YouTube @susangrauofficial
            </a>
            <a
              href="https://www.tiktok.com/@susangrauofficial"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="confirmation-social-tiktok"
              className="btn-ghost-gold rounded-full px-5 py-2.5 text-xs font-medium"
            >
              TikTok @susangrauofficial
            </a>
            <a
              href="https://myinfinite.app"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="confirmation-app-myinfinite"
              className="btn-gold rounded-full px-5 py-2.5 text-xs font-bold"
            >
              Get the MyInfinite App
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Confirmation;
